import { RefObject, useEffect, useState } from 'react';

import { safePauseVideo, safePlayVideo } from '@/utils/videoPlayback/videoPlayback';

import { hideControlsForShortVideos, videoHasAudio } from './VideoContent.helpers';

import type Hls from 'hls.js';

const LOOP_START_TIME = 0;
const PREVIEW_LOOP_END_TIME = 3;
// Decoding the audio track can only be done after the video starts playing.
const AUDIO_DECODE_DELAY_MS = 100;

interface VideoPlaybackOptions {
	autoPlay: boolean;
	hasPoster: boolean;
	isLooped: boolean;
	loadVideoInPreviewMode: boolean;
	youtubeId?: string | null;
}

/** Owns the playback state driven by the video element's own events and viewport visibility. */
export function useVideoContentPlayback(
	videoRef: RefObject<HTMLVideoElement | null>,
	{ autoPlay, hasPoster, isLooped, loadVideoInPreviewMode, youtubeId }: VideoPlaybackOptions
) {
	const [isVideoReady, setIsVideoReady] = useState(false);
	const [isPlaying, setIsPlaying] = useState(autoPlay);
	const [isFirstPlayTriggered, setIsFirstPlayTriggered] = useState(false);
	const [isPosterVisual, setIsPosterVisual] = useState(hasPoster);
	const [isLongVideo, setIsLongVideo] = useState(false);
	const [isAudioControlsVisible, setIsAudioControlsVisible] = useState(true);
	const [userPaused, setUserPaused] = useState(false);

	useEffect(
		function handleVideoEvents() {
			const video = videoRef.current;

			if (!video) {
				return;
			}

			// Arrow consts so TypeScript keeps the non-null narrowing of `video`.
			const handleVideoEnd = () => {
				if (!isLooped) {
					video.currentTime = LOOP_START_TIME;
				}
			};

			function handleVideoPlaying() {
				setIsPlaying(true);
				setIsFirstPlayTriggered(true);
				setIsPosterVisual(false);
				setTimeout(() => setIsAudioControlsVisible(videoHasAudio(videoRef)), AUDIO_DECODE_DELAY_MS);
			}

			function handleVideoPaused() {
				setIsPlaying(false);
			}

			const handleTimeUpdate = () => {
				if (loadVideoInPreviewMode && video.currentTime >= PREVIEW_LOOP_END_TIME) {
					video.currentTime = LOOP_START_TIME;
					setIsPlaying(true);
				}
			};

			function handleLoadedMetadata() {
				setIsLongVideo(hideControlsForShortVideos(videoRef));
				setIsAudioControlsVisible(videoHasAudio(videoRef));
			}

			video.addEventListener('ended', handleVideoEnd);
			video.addEventListener('play', handleVideoPlaying);
			video.addEventListener('pause', handleVideoPaused);
			video.addEventListener('timeupdate', handleTimeUpdate);
			video.addEventListener('loadedmetadata', handleLoadedMetadata);

			// Metadata may already have arrived before this effect ran.
			if (video.readyState > 1) {
				handleLoadedMetadata();
			}

			return () => {
				video.removeEventListener('ended', handleVideoEnd);
				video.removeEventListener('play', handleVideoPlaying);
				video.removeEventListener('pause', handleVideoPaused);
				video.removeEventListener('timeupdate', handleTimeUpdate);
				video.removeEventListener('loadedmetadata', handleLoadedMetadata);
			};
		},
		[isLooped, loadVideoInPreviewMode, videoRef]
	);

	useEffect(
		function handleScrollInOutOfView() {
			const video = videoRef.current;

			if (youtubeId?.trim() || !video) {
				return;
			}

			const shouldPlayWhenVisible = autoPlay || isFirstPlayTriggered;

			const observer = new IntersectionObserver(
				(entries) =>
					entries.forEach((entry) => {
						if (entry.isIntersecting && !userPaused && shouldPlayWhenVisible) {
							safePlayVideo(video);
						} else {
							safePauseVideo(video);
						}
					}),
				{ rootMargin: '0px', threshold: 0.5 }
			);

			observer.observe(video);
			setIsVideoReady(true);

			return () => observer.disconnect();
		},
		[autoPlay, isFirstPlayTriggered, isVideoReady, userPaused, videoRef, youtubeId]
	);

	function togglePlayback() {
		const video = videoRef.current;

		if (!video) {
			return;
		}

		if (isPlaying) {
			safePauseVideo(video);
			setUserPaused(true);
			return;
		}

		safePlayVideo(video);
		setUserPaused(false);
	}

	return {
		isAudioControlsVisible,
		isFirstPlayTriggered,
		isLongVideo,
		isPlaying,
		isPosterVisual,
		isVideoReady,
		setIsVideoReady,
		togglePlayback,
	};
}

/** Attaches an HLS stream, using hls.js where MediaSource is available and Safari's native support otherwise. */
export function useHlsStream(
	videoRef: RefObject<HTMLVideoElement | null>,
	videoSource?: string | null,
	hlsVideoStreamLink?: string | null
) {
	useEffect(() => {
		if (!videoRef.current || videoSource !== 'HLS Streaming' || !hlsVideoStreamLink) {
			return;
		}

		let skip = false;
		let hls: Hls | null = null;

		void import('hls.js').then(async ({ default: HlsOBJ }) => {
			const video = videoRef.current;

			if (skip || !video) {
				return;
			}

			if (!HlsOBJ.isSupported()) {
				// Safari plays HLS natively; without this the element has no source at all.
				if (video.canPlayType('application/vnd.apple.mpegurl')) {
					video.src = hlsVideoStreamLink;
				}
				return;
			}

			// attachMedia swaps the element source, which aborts any pending play().
			await safePauseVideo(video);

			if (skip) {
				return;
			}

			hls = new HlsOBJ();
			hls.loadSource(hlsVideoStreamLink);
			hls.attachMedia(video);
		});

		return () => {
			skip = true;
			hls?.destroy();
		};
	}, [hlsVideoStreamLink, videoRef, videoSource]);
}
